import { WinbackPanel, applyOffer } from '../../../components/admin/billing/WinbackPanel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, Loader2, FileText, Download, Share2, Mail, Plus, Trash2,
  Copy, ChevronDown, ChevronRight, RotateCcw,
  IndianRupee, Send, ShieldCheck, RefreshCw, ImagePlus, ReceiptText, Wand2,
} from 'lucide-react';
import RecordPaymentModal from '../../../components/admin/billing/RecordPaymentModal';
import { PayLinkPanel } from '../../../components/admin/billing/PayLinkPanel';
import { DepositPanel } from '../../../components/admin/billing/DepositPanel';
import InvoiceWarrantyModal from '../../../components/admin/billing/InvoiceWarrantyModal';
import CloneInvoiceModal from '../../../components/admin/billing/CloneInvoiceModal';
import WarrantyPhotosModal from '../../../components/admin/billing/WarrantyPhotosModal';
import ActionMenu from '../../../components/admin/billing/ActionMenu';
import ItemCard from '../../../components/admin/billing/ItemCard';
import { LabourPicker } from '../../../components/admin/billing/LabourPicker';
import { HsnInput, LineTotalInput, useHsnList } from '../../../components/admin/billing/LineInputs';
import { PartPicker, PartChip, partLine, withPartQty } from '../../../components/admin/billing/PartPicker';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { greetName, sharePdfToWhatsApp, renderMessage } from '../../../lib/waShare';
import { sendPaymentThanks } from '../../../lib/paymentThanks';
import { useDeviceDraft } from '../../../lib/useDeviceDraft';
import { DraftBar } from '../../../components/DraftBar';
import {
  fmtINR, formatErr, notifyErr, inputCls, todayISO,
  EST_STATUSES, INV_STATUSES, AutoGrowTextarea,
  r2, computeGstLine, guessHead, REVENUE_HEADS, UNITS,
} from '../../../components/admin/billing/shared';
import { useApiFeedback } from '../../../lib/useApiFeedback';

// Stable per-row id so React reconciles correctly when rows are reordered/deleted.
// Backend-saved rows already carry an `id` — for fresh client rows we mint one.
const newRowId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
  `row_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const noSpin = '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
const emptyItem = () => ({ id: newRowId(), name: '', description: '', qty: 1, rate: 0, hsn_code: '', unit: 'NOS', discount_pct: '', gst_rate: '', line_total_target: '', revenue_head: '' });
const emptyForm = () => ({
  customer_id: '',
  vehicle_ids: [],
  subject: '',
  date: todayISO(),
  due_date: '',
  // Estimate-only: default = date + 7 days (backend defaults the same when
  // omitted). Powers the "Valid until" badge + expiry reminder scheduler.
  validity_date: '',
  items: [emptyItem()],
  advance_received: 0,
  amount_paid: 0,
  notes: '',
  status: 'draft',
  // Estimates are always GST going forward (HSN / Unit / Disc% / GST% columns
  // + one-click convert to GST tax invoice). Legacy non-GST estimates loaded
  // via the form are auto-upgraded (see fetchDoc).
  is_gst_estimate: true,
  // Per-document overrides (null = inherit from global Billing Settings)
  bank_holder_name: null,
  bank_name: null,
  bank_account_number: null,
  bank_ifsc: null,
  upi: null,
  terms_pay: null,
  terms_service: null,
  // Incentive tracking — invoices only. Multi-select of staff who worked on
  // this vehicle; each gets per-vehicle incentive credit when payroll runs.
  worked_on_by_ids: [],
  incentive_per_staff_override: '',  // empty = use each staff's default rate
});

const OVERRIDE_FIELDS = [
  'bank_holder_name', 'bank_name', 'bank_account_number', 'bank_ifsc', 'upi',
  'terms_pay', 'terms_service',
];

const BillingForm = ({ type }) => {
  // type === "estimate" or "invoice"
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const isInvoice = type === 'invoice';
  const isEstimate = !isInvoice;
  const plural = isInvoice ? 'invoices' : 'estimates';
  const Title = isInvoice ? 'Invoice' : 'Estimate';
  const STATUSES = isInvoice ? INV_STATUSES : EST_STATUSES;

  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  // Server-side doc with fields not in the form (payments[], amount_paid, total_due, status, number)
  const [serverDoc, setServerDoc] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [number, setNumber] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const { error: err, showError, showSuccess, clearError, bannerRef: errBannerRef } = useApiFeedback();
  const [emailing, setEmailing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [converted, setConverted] = useState({ to: '', via: '' });
  // Which row's "Total (incl. GST)" box is being edited. While focused the
  // input shows exactly what the user typed (never overwritten by the
  // computed total) so typing/clearing a back-calc figure doesn't glitch.
  const hsnList = useHsnList();
  const [settings, setSettings] = useState(null);  // global Billing Settings (read-only)
  const [showOverrides, setShowOverrides] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [warrantyOpen, setWarrantyOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [refreshingPhotos, setRefreshingPhotos] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/settings`, { headers: authHeader() });
      setSettings(data);
    } catch (e) {
      console.warn('[BillingForm] settings fetch failed:', e?.message || e);
    }
  }, [authHeader]);

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/customers`, { headers: authHeader() });
      setCustomers(data);
    } catch (e) {
      // Non-fatal — the form's customer-select will be empty; the save call
      // will surface a clearer error if the user tries to submit without one.
      console.warn('[BillingForm] customer list fetch failed:', e?.message || e);
    }
  }, [authHeader]);

  const fetchDoc = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/billing/${plural}/${id}`, { headers: authHeader() });
      setForm({
        customer_id: data.customer_id || '',
        winback_code: data.winback_code || '',
        vehicle_ids: Array.isArray(data.vehicle_ids) ? data.vehicle_ids : [],
        subject: data.subject || '',
        date: (data.date || todayISO()).slice(0, 10),
        due_date: (data.due_date || '').slice(0, 10),
        validity_date: (data.validity_date || '').slice(0, 10),
        items: (data.items || []).map((it) => ({
          id: it.id,
          name: it.name || '',
          description: it.description || '',
          qty: it.qty ?? 1,
          rate: it.rate ?? 0,
          hsn_code: it.hsn_code || '',
          chart_price: it.chart_price ?? null,
          unit: it.unit || 'NOS',
          discount_pct: it.discount_pct ?? '',
          gst_rate: it.gst_rate ?? '',
          line_total_target: it.line_total_target && Number(it.line_total_target) > 0
            ? it.line_total_target
            : '',
          revenue_head: it.revenue_head || '',
          part_id: it.part_id || '',
        })),
        advance_received: data.advance_received || 0,
        amount_paid: data.amount_paid || 0,
        notes: data.notes || '',
        status: data.status || 'draft',
        // Estimates are GST-only now: auto-upgrade any legacy non-GST doc
        // opened via the form so users see the same columns and can convert
        // it straight to a GST invoice.
        is_gst_estimate: isEstimate ? true : !!data.is_gst_estimate,
        bank_holder_name: data.bank_holder_name ?? null,
        bank_name: data.bank_name ?? null,
        bank_account_number: data.bank_account_number ?? null,
        bank_ifsc: data.bank_ifsc ?? null,
        upi: data.upi ?? null,
        terms_pay: data.terms_pay ?? null,
        terms_service: data.terms_service ?? null,
        worked_on_by_ids: data.worked_on_by_ids || [],
        incentive_per_staff_override: data.incentive_per_staff_override ?? '',
      });
      setNumber(data.number || '');
      setServerDoc(data);
      // Auto-expand overrides panel if any override is set on this document
      if (OVERRIDE_FIELDS.some((f) => data[f] !== undefined && data[f] !== null)) {
        setShowOverrides(true);
      }
      if (data.converted_invoice_id) {
        setConverted({ to: data.converted_invoice_id, via: 'estimate' });
      }
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  }, [authHeader, id, isNew, plural, showError]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => { fetchDoc(); }, [fetchDoc]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  // Estimates: keep `validity_date` in sync with the estimate date until the
  // user picks a specific one (mirrors the backend default of date + 7 days).
  useEffect(() => {
    if (!isEstimate || !isNew) return;
    if (form.validity_date) return;  // user set it explicitly
    if (!form.date) return;
    const d = new Date(form.date);
    if (Number.isNaN(d.getTime())) return;
    d.setDate(d.getDate() + 7);
    const iso = d.toISOString().slice(0, 10);
    setForm((s) => (s.validity_date ? s : { ...s, validity_date: iso }));
  }, [form.date, form.validity_date, isEstimate, isNew]);
  // Fetch staff list (for the Worked-on-by selector) only on invoices.
  useEffect(() => {
    if (!isInvoice) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/staff`, { headers: authHeader() });
        setStaffList((data || []).filter((s) => s.status === 'active'));
      } catch (e) {
        console.warn('[BillingForm] staff list fetch failed:', e?.message || e);
      }
    })();
  }, [isInvoice, authHeader]);

  const draft = useDeviceDraft(`${isEstimate ? 'estimate' : 'invoice'}:${id || 'new'}`, form, setForm, isNew || (!loading && !!serverDoc));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const hsnTimers = useRef({});
  // Fills an empty HSN from the GST name→HSN rules shortly after the item is typed.
  const autoHsn = (row) => {
    clearTimeout(hsnTimers.current[row.id]);
    const text = `${row.name || ''} ${row.description || ''}`.trim();
    if (!isEstimate || row.hsn_code || text.length < 3) return;
    hsnTimers.current[row.id] = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/gst/hsn/match`, { params: { name: text }, headers: authHeader() });
        if (!data?.hsn_code) return;
        setForm((f) => ({
          ...f,
          items: f.items.map((x) => (x.id === row.id && !x.hsn_code
            ? { ...x, hsn_code: data.hsn_code, ...(Number(x.gst_rate) > 0 ? {} : { gst_rate: data.gst_rate }) }
            : x)),
        }));
      } catch { /* no GST access — leave the field for the user */ }
    }, 600);
  };
  const setItem = (idx, patch) => {
    if ('name' in patch || 'description' in patch) autoHsn({ ...form.items[idx], ...patch });
    setForm((s) => {
      const items = s.items.slice();
      items[idx] = { ...items[idx], ...withPartQty(items[idx], patch) };
      return { ...s, items };
    });
  };
  const addRow = () => setForm((s) => ({ ...s, items: [...s.items, emptyItem()] }));
  const applyWinback = (offer) => {
    if (offer === null) { setForm((s) => ({ ...s, winback_code: '' })); return; }
    const { items, discount } = applyOffer(form.items, offer, { gst: !isInvoice && !!form.is_gst_estimate, makeItem: emptyItem });
    if (!discount) { toast.error('Add items first, then apply the code'); return; }
    setForm((s) => ({ ...s, items, winback_code: offer.code }));
    toast.success(`${offer.code} applied: ₹${discount.toLocaleString('en-IN')} off`);
  };
  // A picked part fills the blank starter row rather than leaving it dangling.
  const labourVehicle = (() => { const c = customers.find((x) => x.id === form.customer_id); return (c?.vehicles || []).find((v) => (form.vehicle_ids || []).includes(String(v.id))) || (c?.vehicles || [])[0]; })();
  const addLabour = (job) => setForm((s) => {
    const line = { ...emptyItem(), name: job.name, description: job.package ? `Part of ${job.package}` : '', hsn_code: job.sac || '', gst_rate: job.gst_rate ?? 18, unit: 'JOB', rate: job.price || 0, chart_price: job.price };
    const items = s.items.slice();
    const last = items[items.length - 1];
    if (last && !last.name && !last.description && !Number(last.rate)) items[items.length - 1] = line;
    else items.push(line);
    return { ...s, items };
  });
  const addPart = (part) => setForm((s) => {
    const line = { ...emptyItem(), ...partLine(part, { taxed: isEstimate && s.is_gst_estimate, split: true }) };
    const items = s.items.slice();
    const last = items[items.length - 1];
    if (last && !last.name && !last.description && !Number(last.rate)) items[items.length - 1] = line;
    else items.push(line);
    return { ...s, items };
  });
  const unlinkPart = (i) => setItem(i, { part_id: '', _stock: null });
  const delRow = (i) => setForm((s) => ({ ...s, items: s.items.filter((_, x) => x !== i) }));

  const totals = useMemo(() => {
    const isGst = isEstimate && form.is_gst_estimate;
    if (isGst) {
      // GST-estimate: subtotal + total reflect the fully computed line totals
      // (honouring per-line round-figure targets).
      let grand = 0;
      let gross = 0;
      (form.items || []).forEach((it) => {
        const line = computeGstLine(it);
        grand += line.total || 0;
        gross += line.gross || 0;
      });
      const advance = Number(form.advance_received || 0);
      return { subtotal: r2(gross), advance, totalDue: r2(grand - advance), grand: r2(grand) };
    }
    const subtotal = (form.items || []).reduce((acc, it) => acc + Number(it.qty || 0) * Number(it.rate || 0), 0);
    const advance = Number(form.advance_received || 0);
    return { subtotal, advance, totalDue: subtotal - advance, grand: subtotal };
  }, [form.items, form.advance_received, form.is_gst_estimate, isEstimate]);

  const save = async () => {
    if (!form.customer_id) { showError('Please select a customer.'); return; }
    clearError();
    setSaving(true);
    try {
      const payload = {
        winback_code: form.winback_code || '',
        customer_id: form.customer_id,
        subject: form.subject,
        date: form.date,
        items: (form.items || []).map((it) => ({
          id: it.id,
          name: it.name,
          description: it.description,
          qty: Number(it.qty || 0),
          rate: Number(it.rate || 0),
          // GST-estimate fields — persisted only when the parent doc opts into
          // "This is a GST estimate", but harmless (defaults) otherwise.
          hsn_code: (it.hsn_code || '').trim(),
          chart_price: it.chart_price ?? null,
          unit: (it.unit || '').trim(),
          discount_pct: it.discount_pct === '' || it.discount_pct == null ? 0 : Number(it.discount_pct),
          gst_rate: it.gst_rate === '' || it.gst_rate == null ? 0 : Number(it.gst_rate),
          line_total_target: it.line_total_target === '' || it.line_total_target == null ? 0 : Number(it.line_total_target),
          revenue_head: (it.revenue_head && it.revenue_head.trim()) || guessHead(`${it.name || ''} ${it.description || ''}`) || '',
          part_id: it.part_id || '',
        })),
        advance_received: Number(form.advance_received || 0),
        notes: form.notes,
        status: form.status,
        // Vehicle picker — empty means "all customer vehicles" (backend default).
        vehicle_ids: Array.isArray(form.vehicle_ids) ? form.vehicle_ids : [],
      };
      if (isInvoice) {
        payload.due_date = form.due_date || null;
        payload.amount_paid = Number(form.amount_paid || 0);
        payload.worked_on_by_ids = form.worked_on_by_ids || [];
        payload.incentive_per_staff_override = form.incentive_per_staff_override !== '' && form.incentive_per_staff_override != null
          ? Number(form.incentive_per_staff_override)
          : null;
      } else {
        // Estimate-only: persist the "This is a GST estimate" toggle so per-line
        // HSN/Unit/Disc%/GST% + tax breakup render on the PDF and one-click
        // convert-to-GST-invoice keeps working.
        payload.is_gst_estimate = !!form.is_gst_estimate;
        // "Valid until" — send when the user has picked one; backend
        // defaults to date + 7 days when omitted.
        if (form.validity_date) {
          payload.validity_date = form.validity_date;
        }
      }
      // Per-document overrides — only include fields the user has explicitly
      // set on this document. null means "inherit from Billing Settings".
      OVERRIDE_FIELDS.forEach((f) => {
        if (form[f] !== undefined && form[f] !== null) {
          payload[f] = form[f];
        }
      });
      let saved;
      if (isNew) {
        ({ data: saved } = await axios.post(`${API}/billing/${plural}`, payload, { headers: authHeader() }));
        draft.clear();
        navigate(`/admin/billing/${plural}/${saved.id}`, { replace: true });
      } else {
        ({ data: saved } = await axios.put(`${API}/billing/${plural}/${id}`, payload, { headers: authHeader() }));
        draft.clear();
        fetchDoc();
      }
      showSuccess(`${Title} saved.`);
      (saved?.stock_warnings || []).forEach((w) => toast.warning(`Running low: ${w}`));
    } catch (e) {
      showError(e);
    } finally {
      setSaving(false);
    }
  };

  // Direct URL — rendered as an <a target="_blank"> on the action bar.
  // Letting the browser navigate directly (instead of fetching → blob → clicking
  // a hidden anchor) keeps the action a trusted user gesture, which avoids
  // popup-blocker false positives in Safari / mobile Chrome.
  const pdfHref = !isNew ? `${API}/billing/${plural}/${id}/pdf` : '#';

  const downloadPdf = async () => {
    if (isNew) { alert('Please save the document first.'); return; }
    try {
      const res = await axios.get(`${API}/billing/${plural}/${id}/pdf`, {
        headers: authHeader(), responseType: 'blob', timeout: 60000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      // Mirror the backend filename convention so downloads match WhatsApp / email attachments
      let suffix = '';
      if (isInvoice && serverDoc?.status === 'paid') suffix = '_PAID';
      else if (isInvoice && serverDoc?.status === 'partially_paid') suffix = '_PARTIALLY_PAID';
      a.download = `${Title}_${number || id.slice(0, 8)}${suffix}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch (e) { notifyErr(e); }
  };

  const customer = customers.find((c) => c.id === form.customer_id);

  // Per-document override helpers. `null` = inherit from global Billing Settings.
  // The UI prefills inputs from settings so the user sees defaults when they
  // first expand the panel; typing then captures an override.
  const settingsFallback = (k) => (settings ? (settings[k] ?? '') : '');
  const effective = (k) => (form[k] !== null && form[k] !== undefined ? form[k] : settingsFallback(k));

  const setOverride = (k, v) => set(k, v);
  const resetOverride = (k) => {
    if (!window.confirm(`Reset "${k}" to Billing Settings default?`)) return;
    set(k, null);
  };

  // For list-style fields (terms_pay / terms_service) we edit as multiline text
  // for simplicity. Convert between array and \n-joined string at the boundary.
  const linesToArray = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean);
  const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');

  const shareWhatsApp = async () => {
    if (isNew) { alert('Please save the document first.'); return; }
    const phone = (customer?.phone || '').replace(/\D/g, '');
    const total = Number(totals.totalDue || 0);
    const paid = Number(serverDoc?.amount_paid || 0);
    const balance = Math.max(0, total - paid);
    const status = serverDoc?.status || '';

    setSharing(true);
    try {
      // Mint a token-protected public URL so the customer can open the PDF
      // without an admin login. The authenticated /pdf route would 401 for
      // them, so a share link is required — not just nicer.
      let pdfUrl = '';
      let approveUrl = '';
      try {
        const { data } = await axios.post(
          `${API}/billing/${plural}/${id}/share-link`, {}, { headers: authHeader() });
        approveUrl = data?.approve_url || '';
        if (data?.pdf_url) {
          // Backend returns a relative path when PUBLIC_BACKEND_URL isn't set;
          // the browser knows its own origin, so resolve it here.
          pdfUrl = data.pdf_url.startsWith('http')
            ? data.pdf_url
            : `${window.location.origin}${data.pdf_url}`;
        }
      } catch (_e) { /* fall through — message still goes out without a link */ }

      // Status-aware message. Estimates get a short ask; invoices get an
      // explicit balance line so the customer immediately knows where they
      // stand without opening the PDF.
      let payLink = ''; let upi = ''; let deposit = '';
      if (isInvoice ? balance > 0 : Number(serverDoc?.deposit_requested || 0) > 0) {
        try {
          const { data: pay } = await axios.get(
            `${API}/payments/${isInvoice ? 'invoice' : 'estimate'}/${id}/pay-link`, { headers: authHeader() });
          payLink = pay?.pay_url || '';
          upi = payLink ? '' : (pay?.upi_vpa || pay?.fallback_upi || '');
          if (!isInvoice && pay?.balance) deposit = fmtINR(pay.balance);
        } catch (_e) { /* no gateway connected — message still goes out */ }
      }
      const key = !isInvoice ? 'estimate'
        : status === 'paid' ? 'invoice_paid'
          : status === 'overdue' ? 'invoice_overdue'
            : status === 'partially_paid' ? 'invoice_partial' : 'invoice_unpaid';
      const message = renderMessage(key, {
        customer: greetName(customer?.prefix, customer?.name), number, doc_type: 'invoice',
        for_subject: form.subject ? ` for ${form.subject}` : '',
        total: fmtINR(total), gst_note: form.is_gst_estimate && !isInvoice ? ' (inclusive of GST)' : '',
        paid: fmtINR(paid), balance: fmtINR(balance || total), advance: '',
        approve_link: !isInvoice ? (approveUrl || '') : '',
        pdf_link: (!isInvoice && approveUrl) ? '' : (pdfUrl || ''),
        deposit,
        pay_link: payLink, upi,
      });
      const filename = `${Title}_${String(number || id.slice(0, 8)).replace(/\//g, '_')}.pdf`;

      await sharePdfToWhatsApp({
        getPdf: async () => (await axios.get(
          pdfUrl || `${API}/billing/${plural}/${id}/pdf`,
          pdfUrl ? { responseType: 'blob' } : { responseType: 'blob', headers: authHeader() },
        )).data,
        filename, title: `${Title} ${number}`, message, phone,
      });
    } catch (e) {
      showError(e);
    } finally {
      setSharing(false);
    }
  };

  const emailToCustomer = async () => {
    if (isNew) { alert('Please save the document first.'); return; }
    if (!customer?.email) { alert('Customer has no email. Add one on the customer profile first.'); return; }
    if (!window.confirm(`Email this ${Title.toLowerCase()} to ${customer.email}?`)) return;
    setEmailing(true);
    try {
      const { data } = await axios.post(`${API}/billing/${plural}/${id}/email`, {}, { headers: authHeader() });
      alert(`${Title} emailed to ${data.sent_to}.`);
      fetchDoc();
    } catch (e) {
      notifyErr(e);
    } finally {
      setEmailing(false);
    }
  };

  const refreshWarrantyPhotos = async () => {
    if (!isInvoice || !serverDoc?.linked_warranty_id) return;
    setRefreshingPhotos(true);
    try {
      const { data } = await axios.post(
        `${API}/billing/invoices/${id}/warranty/refresh-photos`,
        {},
        { headers: authHeader() },
      );
      alert(
        `Photos refreshed for ${data.warranty_id}.\n\n` +
        `${data.after_images_count} after-install + ${data.before_images_count} before-install photos now attached.\n\n` +
        `These appear on the warranty certificate page (now bundled inside the invoice PDF).`,
      );
    } catch (e) {
      notifyErr(e);
    } finally {
      setRefreshingPhotos(false);
    }
  };


  const markSent = async () => {
    if (isNew || !isInvoice) return;
    if (!window.confirm('Mark this invoice as Sent? Use this when you share via WhatsApp or hand over a printout.')) return;
    setMarking(true);
    try {
      await axios.post(`${API}/billing/invoices/${id}/mark-sent`, {}, { headers: authHeader() });
      fetchDoc();
    } catch (e) {
      notifyErr(e);
    } finally {
      setMarking(false);
    }
  };

  const cloneEstimate = async () => {
    if (isInvoice || isNew) return;
    try {
      const { data } = await axios.post(`${API}/billing/estimates/${id}/clone`, {}, { headers: authHeader() });
      navigate(`/admin/billing/estimates/${data.id}`);
    } catch (e) { notifyErr(e); }
  };

  const onInvoiceCloned = (data) => {
    setCloneOpen(false);
    navigate(`/admin/billing/invoices/${data.id}`);
  };

  const [gstConverting, setGstConverting] = useState(false);
  const convertEstimateToGstInvoice = async () => {
    if (isInvoice || isNew) return;
    if (!window.confirm(
      `Convert this estimate to a new GST Tax Invoice?\n\n`
      + `• A draft GST invoice will be created with a fresh UNS/GST/<FY>/<seq> number\n`
      + `• Line items will be carried over with default HSN 998719 + 18% GST\n`
      + `• Existing estimate is NOT modified — you can still convert it normally too\n`
      + `• Open the new GST invoice to tweak HSN codes and tax rates before saving`,
    )) return;
    setGstConverting(true);
    try {
      const { data } = await axios.post(
        `${API}/gst/invoices/from-estimate/${id}`,
        { default_hsn: '998719', default_gst_rate: 18, treat_rates_as: 'exclusive' },
        { headers: authHeader() },
      );
      navigate(`/admin/gst/invoices/${data.id}`);
    } catch (e) { notifyErr(e); }
    finally { setGstConverting(false); }
  };

  if (loading) {
    return <div className="p-10 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" />Loading…</div>;
  }

  return (
    <div className="min-h-screen" onInputCapture={draft.touch} onChangeCapture={draft.touch} onClickCapture={draft.touch}>
      <DraftBar draft={draft} />
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-lg font-semibold tracking-tight truncate text-slate-900" data-testid="form-title">
              {isNew ? `New ${Title}` : `${Title} ${number}`}
            </h1>
            <p className="text-xs text-slate-500 truncate">
              {isNew ? `Create a new ${Title.toLowerCase()}` : `Customer: ${customer?.name || '—'} · Status: ${form.status}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {!isNew && (
              <>
                {/* Primary: View PDF (always visible) */}
                <a href={pdfHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-2.5 sm:px-3 py-2 rounded-lg" data-testid="view-pdf-btn">
                  <FileText size={14} /> <span className="hidden sm:inline">View </span>PDF
                </a>

                {/* Primary: WhatsApp share (most-used handoff path) */}
                <button onClick={shareWhatsApp} disabled={sharing} className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-emerald-100 hover:bg-emerald-200 disabled:opacity-60 border border-emerald-300 text-emerald-700 px-2.5 sm:px-3 py-2 rounded-lg" data-testid="share-whatsapp-btn">
                  {sharing ? <Loader2 className="animate-spin" size={14} /> : <Share2 size={14} />}
                  <span className="hidden sm:inline">{sharing ? 'Preparing…' : 'WhatsApp'}</span>
                </button>

                {/* Primary: Record Payment (invoices only, status-aware) */}
                {isInvoice && serverDoc?.status !== 'cancelled' && (
                  <button
                    onClick={() => setPaymentOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-500 px-2.5 sm:px-3 py-2 rounded-lg shadow-sm"
                    data-testid="record-payment-btn"
                  >
                    <IndianRupee size={14} />
                    <span className="hidden sm:inline">{serverDoc?.status === 'paid' ? 'Payments' : 'Record Payment'}</span>
                  </button>
                )}

                {/* Primary: linked warranty badge (only when one already exists) */}
                {isInvoice && serverDoc?.linked_warranty_id && (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-teal-50 border border-teal-300 text-teal-700 pl-2.5 pr-1 sm:pl-3 py-1 rounded-lg font-mono"
                    title="eWarranty linked to this invoice — warranty page is appended to the invoice PDF"
                    data-testid="warranty-linked-badge"
                  >
                    <ShieldCheck size={14} />
                    <span>{serverDoc.linked_warranty_id}</span>
                    <a
                      href={`${API}/warranty/${serverDoc.linked_warranty_id}/certificate`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 p-1 rounded hover:bg-teal-100 text-teal-700"
                      title="Download warranty certificate only (PDF)"
                      data-testid="warranty-cert-download-btn"
                    >
                      <Download size={13} />
                    </a>
                    <button
                      onClick={() => setPhotosOpen(true)}
                      className="p-1 rounded hover:bg-teal-100 text-teal-700"
                      title="Manage warranty photos (upload / remove)"
                      data-testid="warranty-manage-photos-btn"
                    >
                      <ImagePlus size={13} />
                    </button>
                    <button
                      onClick={refreshWarrantyPhotos}
                      disabled={refreshingPhotos}
                      className="p-1 rounded hover:bg-teal-100 text-teal-700 disabled:opacity-50"
                      title="Pull installation photos from the linked inspection"
                      data-testid="warranty-refresh-photos-btn"
                    >
                      {refreshingPhotos ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />}
                    </button>
                  </span>
                )}

                {/* Overflow menu: secondary actions tucked away to declutter the bar */}
                <ActionMenu testid="billing-more">
                  <ActionMenu.Item icon={<Download size={14} />} onClick={downloadPdf} testid="more-download">
                    Download PDF
                  </ActionMenu.Item>
                  <ActionMenu.Item
                    icon={emailing ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />}
                    onClick={emailToCustomer}
                    disabled={emailing}
                    testid="more-email"
                  >
                    {emailing ? 'Sending…' : 'Email to customer'}
                  </ActionMenu.Item>
                  {isInvoice && serverDoc?.status === 'draft' && (
                    <ActionMenu.Item
                      icon={marking ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      onClick={markSent}
                      disabled={marking}
                      tone="warning"
                      testid="more-mark-sent"
                    >
                      Mark as Sent
                    </ActionMenu.Item>
                  )}
                  {isInvoice && serverDoc?.status !== 'cancelled' && !serverDoc?.linked_warranty_id && (
                    <ActionMenu.Item
                      icon={<ShieldCheck size={14} />}
                      onClick={() => setWarrantyOpen(true)}
                      tone="primary"
                      testid="more-create-warranty"
                    >
                      Create eWarranty
                    </ActionMenu.Item>
                  )}
                  {isInvoice && (
                    <ActionMenu.Item
                      icon={<Copy size={14} />}
                      onClick={() => setCloneOpen(true)}
                      testid="more-clone-invoice"
                    >
                      Clone invoice…
                    </ActionMenu.Item>
                  )}
                  {!isInvoice && (
                    <>
                      <ActionMenu.Item icon={<Copy size={14} />} onClick={cloneEstimate} testid="more-clone-estimate">
                        Clone estimate
                      </ActionMenu.Item>
                      <ActionMenu.Item
                        icon={gstConverting ? <Loader2 className="animate-spin" size={14} /> : <ReceiptText size={14} />}
                        onClick={convertEstimateToGstInvoice}
                        tone="primary"
                        testid="more-convert-to-gst-invoice"
                      >
                        Convert to GST invoice
                      </ActionMenu.Item>
                    </>
                  )}
                </ActionMenu>
              </>
            )}
            <button onClick={save} disabled={saving || !form.customer_id} className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 sm:px-4 py-2 rounded-lg" data-testid="save-btn">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5 pb-28 md:pb-6">
        {err && (
          <div ref={errBannerRef} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-start justify-between gap-2" role="alert" aria-live="assertive" data-testid="billing-form-error">
            <span className="flex-1">{err}</span>
            <button type="button" onClick={clearError} className="text-red-500 hover:text-red-700 shrink-0" aria-label="Dismiss error">✕</button>
          </div>
        )}
        {converted.to && (
          <div className="text-sm bg-violet-50 border border-violet-200 text-violet-800 rounded px-3 py-2">
            This estimate has been converted to an invoice.{' '}
            <button onClick={() => navigate(`/admin/billing/invoices/${converted.to}`)} className="underline font-medium">
              Open invoice →
            </button>
          </div>
        )}

        {/* Top meta panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Customer *</label>
              <select value={form.customer_id} onChange={(e) => {
                const newId = e.target.value;
                setForm((s) => ({ ...s, customer_id: newId, vehicle_ids: [], winback_code: s.customer_id === newId ? s.winback_code : '' }));
              }} className={inputCls} data-testid="customer-select">
                <option value="">— Select customer —</option>
                {customers.map((c) => {
                  const display = [c.prefix, c.name].filter(Boolean).join(' ');
                  return <option key={c.id} value={c.id}>{display}{c.phone ? ` · ${c.phone}` : ''}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{Title} date</label>
              <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className={inputCls} data-testid="doc-date" />
            </div>
            {isInvoice && (
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Due date</label>
                <input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} className={inputCls} data-testid="due-date" />
              </div>
            )}
            {isEstimate && (
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Valid until</label>
                <input
                  type="date"
                  value={form.validity_date || ''}
                  min={form.date}
                  onChange={(e) => set('validity_date', e.target.value)}
                  className={inputCls}
                  data-testid="validity-date"
                />
                <div className="mt-1 text-[10px] text-slate-500">
                  Defaults to 7 days after the estimate date.
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)} className={inputCls} data-testid="status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Subject</label>
              <input value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="e.g. Hyundai Accent Restoration & Painting" className={inputCls} data-testid="subject" />
            </div>
            {/* Vehicle picker — appears only after a customer is selected AND the
                customer has at least one vehicle on file. Empty selection ⇒
                the doc covers all customer vehicles (legacy behaviour). */}
            {form.customer_id && (customer?.vehicles?.length || 0) > 0 && (
              <div className="sm:col-span-2 lg:col-span-4">
                <div className="flex items-baseline justify-between mb-1">
                  <label className="block text-xs uppercase tracking-wider text-slate-500">
                    Vehicle(s) for this {Title.toLowerCase()}
                  </label>
                  <span className="text-[10px] text-slate-400">
                    Leave all unchecked to include every vehicle on file
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="vehicle-picker">
                  {(customer.vehicles || []).map((v) => {
                    const vid = String(v.id || '');
                    if (!vid) return null;
                    const checked = (form.vehicle_ids || []).includes(vid);
                    const parts = [v.make, v.model].filter(Boolean).join(' ') || 'Vehicle';
                    const label = v.registration_number ? `${parts} — ${v.registration_number}` : parts;
                    return (
                      <label
                        key={vid}
                        className={`inline-flex items-center gap-2 border rounded-full px-3 py-1.5 text-xs cursor-pointer select-none transition ${
                          checked
                            ? 'bg-teal-50 border-teal-300 text-teal-800'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid={`vehicle-chip-${vid}`}
                      >
                        <input
                          type="checkbox"
                          className="accent-teal-600"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(form.vehicle_ids || []);
                            if (e.target.checked) next.add(vid); else next.delete(vid);
                            set('vehicle_ids', Array.from(next));
                          }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-slate-900">Items</h2>
              {isEstimate && (
                <span className="hidden sm:inline-block text-[10px] uppercase tracking-wider bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded" data-testid="est-gst-badge">
                  GST · HSN / Unit / Disc % / GST %
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <PartPicker onPick={addPart} />
              <LabourPicker vehicle={labourVehicle} onPick={addLabour} />
              <button onClick={addRow} className="inline-flex items-center gap-1.5 text-xs bg-teal-50 hover:bg-teal-100 border border-teal-200 text-teal-700 px-3 py-1.5 rounded-lg" data-testid="add-item-btn">
                <Plus size={13} /> Add row
              </button>
            </div>
          </div>
          <WinbackPanel customerId={form.customer_id} applied={form.winback_code} onApply={applyWinback} />
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 w-10">#</th>
                  <th className="text-left px-3 py-2">Item &amp; Description</th>
                  {isEstimate && <th className="text-left px-2 py-2 w-32">HSN/SAC</th>}
                  {isEstimate && form.is_gst_estimate && <th className="text-left px-2 py-2 w-24">Unit</th>}
                  <th className="text-right px-3 py-2 w-20">Qty</th>
                  <th className="text-right px-3 py-2 w-32">Rate (₹)</th>
                  {isEstimate && form.is_gst_estimate && (
                    <>
                      <th className="text-right px-2 py-2 w-20">Disc %</th>
                      <th className="text-right px-2 py-2 w-20">GST %</th>
                    </>
                  )}
                  <th className="text-right px-3 py-2 w-40">{isEstimate && form.is_gst_estimate ? 'Total (incl. GST)' : 'Amount'}</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => {
                  const isGstRow = isEstimate && form.is_gst_estimate;
                  const line = isGstRow ? computeGstLine(it) : null;
                  const hasTarget = isGstRow && it.line_total_target !== '' && it.line_total_target != null && Number(it.line_total_target) > 0;
                  const qtyN = Number(it.qty) || 0;
                  const plainAmount = (Number(it.qty || 0) * Number(it.rate || 0)) || 0;
                  const rateDisplay = hasTarget
                    ? (qtyN ? r2(line.gross / qtyN) : line.gross)
                    : it.rate;
                  const amountDisplay = isGstRow ? (line.total || 0) : plainAmount;
                  return (
                    <tr key={it.id || `row-${i}`} className="border-t border-slate-200 align-top">
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2 space-y-1">
                        <AutoGrowTextarea
                          minRows={1}
                          value={it.name}
                          onChange={(e) => setItem(i, { name: e.target.value })}
                          placeholder="Item name"
                          className={`${inputCls} resize-none leading-snug overflow-hidden`}
                          data-testid={`item-name-${i}`}
                        />
                        <PartChip item={it} index={i} onUnlink={() => unlinkPart(i)} />
                        {it.chart_price != null && Number(it.rate) !== Number(it.chart_price) && !it.line_total_target && (
                          <span className="inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800" title={`Labour chart price ₹${it.chart_price}`} data-testid={`item-chart-flag-${i}`}>changed from chart (₹{it.chart_price})</span>
                        )}
                        <AutoGrowTextarea
                          minRows={3}
                          value={it.description}
                          onChange={(e) => setItem(i, { description: e.target.value })}
                          placeholder="Optional description (multi-line, e.g. specifications, notes)"
                          className={`${inputCls} text-xs resize-y leading-snug overflow-hidden`}
                          data-testid={`item-desc-${i}`}
                        />
                        {isEstimate && (
                          <div className="mt-1">
                            <select
                              value={it.revenue_head || guessHead(`${it.name || ''} ${it.description || ''}`) || ''}
                              onChange={(e) => setItem(i, { revenue_head: e.target.value })}
                              className="w-full text-[11px] bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-slate-700"
                              data-testid={`item-head-${i}`}
                              title="Revenue head — used in the pipeline / revenue-heads report"
                            >
                              <option value="">— Revenue head (auto-suggested) —</option>
                              {REVENUE_HEADS.map((h) => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </td>
                      {isEstimate && (
                        <td className="px-2 py-2">
                          <HsnInput value={it.hsn_code} list={hsnList} onPick={(patch) => setItem(i, patch)}
                            className={`${inputCls} font-mono`} testId={`item-hsn-${i}`} />
                        </td>
                      )}
                      {isEstimate && form.is_gst_estimate && (
                        <>
                          <td className="px-2 py-2">
                            <select
                              value={it.unit || 'NOS'}
                              onChange={(e) => setItem(i, { unit: e.target.value })}
                              className={inputCls}
                              data-testid={`item-unit-${i}`}
                            >
                              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number" min="0" step="0.01"
                          value={it.qty}
                          onChange={(e) => setItem(i, { qty: e.target.value })}
                          className={`${inputCls} text-right ${noSpin}`}
                          data-testid={`item-qty-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number" min="0" step="0.01"
                          value={rateDisplay}
                          onChange={(e) => setItem(i, { rate: e.target.value })}
                          disabled={hasTarget}
                          className={`${inputCls} text-right ${hasTarget ? 'bg-slate-100 text-slate-500 font-mono' : ''}`}
                          title={hasTarget ? 'Auto-calculated from the locked line total' : undefined}
                          data-testid={`item-rate-${i}`}
                        />
                      </td>
                      {isEstimate && form.is_gst_estimate && (
                        <>
                          <td className="px-2 py-2">
                            <input
                              type="number" min="0" step="0.01"
                              value={it.discount_pct ?? ''}
                              onChange={(e) => setItem(i, { discount_pct: e.target.value })}
                              className={`${inputCls} text-right`}
                              placeholder="0"
                              data-testid={`item-disc-${i}`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number" min="0" step="0.01"
                              value={it.gst_rate ?? ''}
                              onChange={(e) => setItem(i, { gst_rate: e.target.value })}
                              className={`${inputCls} text-right`}
                              placeholder="18"
                              data-testid={`item-gst-${i}`}
                            />
                          </td>
                        </>
                      )}
                      {isGstRow ? (
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1">
                            <LineTotalInput
                              target={it.line_total_target}
                              computed={line.total}
                              onChange={(v) => setItem(i, { line_total_target: v })}
                              className={`${inputCls} text-right font-mono font-bold ${hasTarget ? 'border-teal-400 bg-teal-50 text-teal-800' : 'text-teal-700'}`}
                              title="Line total incl. GST — type a round figure and the rate back-calculates exactly"
                              data-testid={`item-total-${i}`}
                            />
                            <button
                              type="button"
                              onClick={() => line.total > 0 && setItem(i, { line_total_target: String(Math.round(line.total)) })}
                              className="p-1 rounded hover:bg-teal-100 text-teal-600 shrink-0"
                              title="Round off to nearest ₹"
                              data-testid={`item-round-${i}`}
                            >
                              <Wand2 size={13} />
                            </button>
                          </div>
                          {hasTarget && (
                            <button
                              type="button"
                              onClick={() => setItem(i, { line_total_target: '' })}
                              className="mt-1 text-[10px] text-slate-400 hover:text-red-500 underline"
                              data-testid={`item-total-clear-${i}`}
                            >
                              clear — rate drives total
                            </button>
                          )}
                        </td>
                      ) : (
                        <td className="px-3 py-2 text-right text-slate-900 font-semibold whitespace-nowrap">{fmtINR(amountDisplay)}</td>
                      )}
                      <td className="px-3 py-2 text-right">
                        {form.items.length > 1 && (
                          <button onClick={() => delRow(i)} className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Remove row" data-testid={`del-item-${i}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Phone / small-tablet view — the table above is unreachable below md */}
          {isEstimate && form.items.some((it) => it.chart_price != null) && (() => {
            const ls = form.items.filter((it) => it.chart_price != null);
            const billed = ls.reduce((a, it) => a + Number(it.qty || 0) * Number(it.rate || 0), 0);
            const chart = ls.reduce((a, it) => a + Number(it.qty || 0) * Number(it.chart_price || 0), 0);
            return (
              <div className="border-t border-slate-200 px-4 py-2 text-xs text-slate-600" data-testid="labour-vs-chart">
                Labour vs chart: <b>{fmtINR(billed)}</b> billed vs <b>{fmtINR(chart)}</b> on chart
                <span className={billed < chart ? 'ml-2 font-semibold text-red-600' : 'ml-2 font-semibold text-emerald-700'}>{billed === chart ? '(at chart)' : `(${billed < chart ? '' : '+'}${fmtINR(billed - chart)})`}</span>
              </div>
            );
          })()}
          <div className="md:hidden" data-testid="items-card-list">
            {form.items.map((it, i) => (
              <ItemCard
                key={it.id || `card-${i}`}
                item={it}
                index={i}
                isGstRow={isEstimate && form.is_gst_estimate}
                showRevenueHead={isEstimate}
                showHsn={isEstimate}
                canDelete={form.items.length > 1}
                onChange={(patch) => setItem(i, patch)}
                onDelete={() => delRow(i)}
                onUnlinkPart={() => unlinkPart(i)}
                hsnList={hsnList}
              />
            ))}
            <button
              onClick={addRow}
              className="w-full flex items-center justify-center gap-2 border-t border-slate-200 py-3.5 text-sm font-semibold text-teal-700 hover:bg-teal-50"
              data-testid="add-item-btn-mobile"
            >
              <Plus size={16} /> Add another item
            </button>
          </div>
        </div>

        {/* Bank & Terms overrides (this document only) */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowOverrides((s) => !s)}
            className="w-full flex items-center justify-between px-4 sm:px-5 py-3 text-left hover:bg-slate-50"
            data-testid="toggle-overrides"
          >
            <div className="flex items-center gap-2">
              {showOverrides ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span className="text-sm font-semibold text-slate-900">Bank &amp; Terms (this {Title.toLowerCase()} only)</span>
              {OVERRIDE_FIELDS.some((f) => form[f] !== null && form[f] !== undefined) && (
                <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">customized</span>
              )}
            </div>
            <span className="text-xs text-slate-500">Defaults pulled from Billing Setup</span>
          </button>

          {showOverrides && (
            <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                Edits here only change this {Title.toLowerCase()}&apos;s PDF. To change defaults for every document, edit <b>Billing Setup</b>.
              </p>

              {/* Bank section */}
              <div>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Bank details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    ['bank_holder_name', 'Account holder name'],
                    ['bank_name', 'Bank name'],
                    ['bank_account_number', 'Account number'],
                    ['bank_ifsc', 'IFSC code'],
                    ['upi', 'UPI / GPay / PhonePe'],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <label className="block text-[11px] text-slate-500 mb-1">{label}</label>
                      <div className="relative">
                        <input
                          value={effective(k)}
                          onChange={(e) => setOverride(k, e.target.value)}
                          className={`${inputCls} ${form[k] !== null && form[k] !== undefined ? 'pr-9 border-amber-400' : ''}`}
                          placeholder={settingsFallback(k) || ''}
                          data-testid={`override-${k}`}
                        />
                        {form[k] !== null && form[k] !== undefined && (
                          <button
                            type="button"
                            onClick={() => resetOverride(k)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
                            title="Reset to default"
                            data-testid={`reset-${k}`}
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Terms section */}
              {[
                ['terms_pay', 'Payment terms'],
                ['terms_service', 'Service terms & conditions'],
              ].map(([k, label]) => {
                const overridden = form[k] !== null && form[k] !== undefined;
                const text = overridden ? arrayToLines(form[k]) : arrayToLines(settings?.[k]);
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</label>
                      {overridden && (
                        <button
                          type="button"
                          onClick={() => resetOverride(k)}
                          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
                          data-testid={`reset-${k}`}
                        >
                          <RotateCcw size={11} /> Reset to default
                        </button>
                      )}
                    </div>
                    <textarea
                      rows={Math.min(10, Math.max(3, (text.match(/\n/g)?.length || 0) + 2))}
                      value={text}
                      onChange={(e) => setOverride(k, linesToArray(e.target.value))}
                      className={`${inputCls} ${overridden ? 'border-amber-400' : ''}`}
                      placeholder="One line per term…"
                      data-testid={`override-${k}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Totals + Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Notes</h2>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Internal notes / customer-facing remarks…"
              className={inputCls}
              data-testid="notes"
            />
            {isInvoice && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Worked on by (Incentive credit)</h3>
                {staffList.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No active staff. Add staff in <a href="/admin/staff" className="text-teal-700 underline">Staff &amp; Attendance</a> first.</p>
                ) : (
                  <>
                    <div className="space-y-1 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50">
                      {staffList.map((s) => {
                        const checked = (form.worked_on_by_ids || []).includes(s.id);
                        return (
                          <label key={s.id} className="flex items-center justify-between gap-2 px-2 py-1 hover:bg-white rounded cursor-pointer text-sm">
                            <span className="flex items-center gap-2">
                              <input type="checkbox" checked={checked} onChange={() => {
                                const cur = form.worked_on_by_ids || [];
                                set('worked_on_by_ids', checked ? cur.filter((x) => x !== s.id) : [...cur, s.id]);
                              }} data-testid={`workedby-${s.id}`} />
                              <span>{s.name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.level === 'senior' ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-600'}`}>{s.level}</span>
                            </span>
                            <span className="text-[11px] text-slate-500 font-mono">₹{s.incentive_per_vehicle}/veh</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <label className="text-slate-600">Override rate (₹ per staff):</label>
                      <input type="number" min={0} value={form.incentive_per_staff_override ?? ''} onChange={(e) => set('incentive_per_staff_override', e.target.value)} placeholder="Default" className={`${inputCls} max-w-[120px]`} data-testid="incentive-override" />
                      <span className="text-[11px] text-slate-400">Blank = each staff&apos;s own rate</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Sub Total</span>
                <span className="font-semibold text-slate-900">₹ {fmtINR(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Advance received</span>
                <input
                  type="number" min="0" step="0.01"
                  value={form.advance_received}
                  onChange={(e) => set('advance_received', e.target.value)}
                  className={`${inputCls} text-right max-w-[140px]`}
                  data-testid="advance-input"
                />
              </div>
              {isEstimate && Number(serverDoc?.deposit_paid || 0) > 0 && (
                <div className="flex justify-between text-xs text-emerald-700" data-testid="summary-deposit-collected">
                  <span>Advance collected (see below)</span>
                  <span className="font-semibold">₹ {fmtINR(serverDoc.deposit_paid)} · comes off the invoice</span>
                </div>
              )}
              {isInvoice && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Amount paid</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.amount_paid}
                    onChange={(e) => set('amount_paid', e.target.value)}
                    className={`${inputCls} text-right max-w-[140px]`}
                    data-testid="paid-input"
                  />
                </div>
              )}
              <div className="border-t border-slate-200 my-2" />
              <div className="flex justify-between text-base">
                <span className="font-semibold text-slate-900">Total Due</span>
                <span className="font-bold text-slate-900">₹ {fmtINR(totals.totalDue)}</span>
              </div>
            </div>
          </div>
        </div>
        {!isNew && serverDoc && (
          <div className="mt-4">
            {isInvoice
              ? <PayLinkPanel invoiceId={id} refreshKey={serverDoc.amount_paid} />
              : <DepositPanel estimateId={id} onPaid={fetchDoc} customer={customer} vehicle={labourVehicle} number={serverDoc.number || ""} total={Number(serverDoc.total_due || 0)} />}
          </div>
        )}
      </main>

      {/* Sticky action bar — on a phone the Save button at the top of a long
          form is a scroll away, and the running total is at the very bottom.
          This keeps both in reach at all times. */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        data-testid="mobile-action-bar"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Total due</div>
          <div className="text-lg font-bold text-slate-900 tabular-nums truncate" data-testid="mobile-total-due">
            ₹ {fmtINR(totals.totalDue)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isNew && (
            <button
              onClick={shareWhatsApp}
              disabled={sharing}
              className="inline-flex items-center gap-1.5 bg-emerald-100 hover:bg-emerald-200 disabled:opacity-60 border border-emerald-300 text-emerald-700 font-semibold px-4 py-3 rounded-xl"
              data-testid="mobile-share-whatsapp-btn"
            >
              {sharing ? <Loader2 className="animate-spin" size={16} /> : <Share2 size={16} />}
              {sharing ? 'Preparing…' : 'Send'}
            </button>
          )}
          <button
            onClick={save}
            disabled={saving || !form.customer_id}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-5 py-3 rounded-xl"
            data-testid="mobile-save-btn"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {paymentOpen && isInvoice && serverDoc && (
        <RecordPaymentModal
          invoice={serverDoc}
          onClose={() => setPaymentOpen(false)}
          onSendThanks={(inv, payment) => sendPaymentThanks({ invoice: inv, payment, gst: false, customer, authHeader }).catch(showError)}
          onSaved={(updated) => {
            // Reflect new amount_paid/status/payments in parent without
            // closing the modal — user may want to add more.
            setServerDoc(updated);
            setForm((f) => ({ ...f, amount_paid: updated.amount_paid || 0, status: updated.status || f.status }));
          }}
        />
      )}
      {warrantyOpen && isInvoice && serverDoc && (
        <InvoiceWarrantyModal
          invoice={serverDoc}
          customer={customer}
          onClose={() => setWarrantyOpen(false)}
          onCreated={(data) => {
            setServerDoc((d) => ({ ...d, linked_warranty_id: data.warranty_id }));
          }}
        />
      )}
      {cloneOpen && isInvoice && serverDoc && (
        <CloneInvoiceModal
          invoice={serverDoc}
          sourceCustomer={customer}
          onClose={() => setCloneOpen(false)}
          onCloned={onInvoiceCloned}
        />
      )}
      {photosOpen && isInvoice && serverDoc?.linked_warranty_id && (
        <WarrantyPhotosModal
          warrantyId={serverDoc.linked_warranty_id}
          onClose={() => setPhotosOpen(false)}
        />
      )}
    </div>
  );
};

export default BillingForm;
